import { useEffect, useRef, useState } from "react";
import {
  IconArrowBackUp,
  IconArrowsJoin,
  IconArrowsLeftRight,
  IconArrowLeft,
  IconArrowRampRight,
  IconArrowRight,
  IconArrowUp,
  IconChevronDown,
  IconGitBranch,
  IconHome2,
  IconParking,
  IconBuildingStore,
  IconBuildingSkyscraper,
  IconMapPin,
  IconRoad,
  IconRotateClockwise2,
  IconTrash,
  IconToolsKitchen2,
} from "@tabler/icons-react";
import InlineError from "@components/forms/InlineError";
import AppModal from "@components/modals/AppModal";
import MediaPicker from "@components/media/MediaPicker";
import useStartPointLanguages from "@hooks/start_point/useStartPointLanguages";
import {
  createQuillEditor,
  destroyQuillEditor,
  getQuillHtml,
} from "@utils/quill/editor";
import { resolveMediaKind } from "@utils/media/mediaUrl";
import { TURN_TYPE_DEFINITIONS } from "@utils/mode_tool/turnType";
import {
  hasRoutePointMedia,
  hasRoutePointName,
  hasRoutePointRadius,
  hasRoutePointTurnCase,
  hasRoutePointType,
  isNewRoutePoint,
} from "@utils/mode_tool/validation";
import {
  buildDrivingRouteGroupName,
  DRIVING_ALTERNATE_ROUTE_KEYS,
  normalizeDrivingRouteGroupKey,
} from "@utils/mode_tool/modeRoute";

const TURN_TYPE_ICON_BY_KEY = {
  point: IconMapPin,
  continueStraight: IconArrowUp,
  rightTurn: IconArrowRight,
  leftTurn: IconArrowLeft,
  uTurn: IconArrowBackUp,
  roundaboutTurn: IconRotateClockwise2,
  mergeTurn: IconArrowsJoin,
  slipRoadTurn: IconGitBranch,
  laneChangeFromRight: IconRoad,
  laneChangeFromLeft: IconRoad,
  offsetTurn: IconArrowRampRight,
  dividedHighwayCrossover: IconArrowsLeftRight,
  parking: IconParking,
};

const POINT_TYPE_OPTIONS = [
  { value: "building", label: "Building", Icon: IconBuildingSkyscraper },
  { value: "parking", label: "Parking", Icon: IconParking },
  { value: "restaurant", label: "Restaurant", Icon: IconToolsKitchen2 },
  { value: "hotel", label: "Hotel", Icon: IconHome2 },
];

function readLocalizedValue(entry) {
  if (typeof entry === "string") return entry;
  if (entry && typeof entry === "object") return entry.value || "";
  return "";
}

function getRouteGroupSelectValue(point = {}) {
  return normalizeDrivingRouteGroupKey(
    point?.routeGroup ?? point?.route_group ?? "",
    point?.routeGroupName ?? point?.route_group_name ?? "",
    Number(point?.order || 0) || 0,
  );
}

function ModeToolPointEditor({
  point,
  allModePoints,
  selectedMode,
  languages,
  activeLanguageIso,
  onCommitPoint,
  onUploadPointMedia,
  onDeletePoint,
}) {
  const languageList = useStartPointLanguages(languages);
  const [draft, setDraft] = useState(point || null);
  const [editor, setEditor] = useState({
    isOpen: false,
    languageIso: "",
    field: "description",
  });
  const [editorHtml, setEditorHtml] = useState("");
  const [quillHost, setQuillHost] = useState(null);
  const quillInstanceRef = useRef(null);
  const quillHandlerRef = useRef(null);
  const [isDescriptionEditorReady, setIsDescriptionEditorReady] =
    useState(false);
  const [touched, setTouched] = useState({
    name: false,
    radiusMeters: false,
    turnCase: false,
    pointType: false,
  });
  const [isTurnTypePickerOpen, setIsTurnTypePickerOpen] = useState(false);
  const [isRouteGroupPickerOpen, setIsRouteGroupPickerOpen] = useState(false);
  const [isPointTypePickerOpen, setIsPointTypePickerOpen] = useState(false);

  useEffect(() => {
    // Local draft mirrors selected point so editing stays responsive while typing.
    setDraft(point || null);
  }, [point]);

  useEffect(() => {
    if (selectedMode !== "random") return;
    if (!draft?.id) return;
    if (String(draft?.pointType || "").trim()) return;
    commitDraftImmediately({
      ...(draft || {}),
      pointType: POINT_TYPE_OPTIONS[0]?.value || "building",
    });
  }, [draft, selectedMode]);

  function openLocalizedEditor(field, languageIso) {
    setEditor({ isOpen: true, languageIso, field });
    setEditorHtml(readLocalizedValue(draft?.[field]?.[languageIso]));
  }

  function closeLocalizedEditor() {
    setEditor({ isOpen: false, languageIso: "", field: "description" });
    setEditorHtml("");
    setIsDescriptionEditorReady(false);
  }

  function saveLocalizedEditor() {
    if (!draft?.id || !editor.languageIso) return;
    const nextDraft = {
      ...(draft || {}),
      [editor.field]: {
        ...(draft?.[editor.field] || {}),
        [editor.languageIso]: editorHtml,
      },
    };
    commitDraftImmediately(nextDraft);
    closeLocalizedEditor();
  }

  useEffect(() => {
    if (!editor.isOpen) return;
    if (!quillHost || quillInstanceRef.current) return;
    const quill = createQuillEditor({
      container: quillHost,
      initialHtml: editorHtml,
    });
    if (!quill) return;
    quillHandlerRef.current = () => setEditorHtml(getQuillHtml(quill));
    quill.on("text-change", quillHandlerRef.current);
    quillInstanceRef.current = quill;
    setIsDescriptionEditorReady(true);
  }, [editor.isOpen, editorHtml, quillHost]);

  useEffect(() => {
    if (!editor.isOpen && quillInstanceRef.current) {
      destroyQuillEditor({
        container: quillHost,
        quill: quillInstanceRef.current,
        textChangeHandler: quillHandlerRef.current,
      });
      quillInstanceRef.current = null;
      quillHandlerRef.current = null;
      setIsDescriptionEditorReady(false);
      setQuillHost(null);
    }
  }, [editor.isOpen, quillHost]);

  if (!draft) return null;

  function commitDraftImmediately(nextDraft) {
    setDraft(nextDraft);
    if (!nextDraft?.id) return;
    onCommitPoint?.(nextDraft.id, {
      ...nextDraft,
      isPersisted: true,
    });
  }

  const currentLanguageIso = languageList.some(
    (language) => language.iso === activeLanguageIso,
  )
    ? activeLanguageIso
    : languageList[0]?.iso || "";
  const currentLanguageLabel =
    languageList.find((language) => language.iso === currentLanguageIso)
      ?.name || currentLanguageIso.toUpperCase();
  const drivingRouteGroupOptions =
    selectedMode === "driving" ? DRIVING_ALTERNATE_ROUTE_KEYS : [];
  const routeGroupSelectValue = getRouteGroupSelectValue(draft);

  const radiusValue = Number(draft?.radiusMeters ?? 0);
  const turnCaseValue = Number(draft?.turnCase);
  const activeTurnType =
    TURN_TYPE_DEFINITIONS.find(
      (option) => Number(option.value) === Number(turnCaseValue),
    ) || null;
  const ActiveTurnTypeIcon =
    TURN_TYPE_ICON_BY_KEY[activeTurnType?.key] || IconMapPin;
  const activePointType =
    POINT_TYPE_OPTIONS.find(
      (option) => String(option.value) === String(draft?.pointType || ""),
    ) || null;
  const ActivePointTypeIcon = activePointType?.Icon || IconHome2;
  const radiusError = hasRoutePointRadius(radiusValue)
    ? ""
    : "Radius must be greater than 0.";
  const currentNameValue = readLocalizedValue(
    draft?.name?.[currentLanguageIso],
  );
  const englishNameValue = readLocalizedValue(draft?.name?.en);
  const isNewPoint = isNewRoutePoint(draft);
  const nameError =
    isNewPoint &&
    currentLanguageIso === "en" &&
    !String(englishNameValue || "").trim()
      ? "EN name is required."
      : "";
  const caseError = hasRoutePointTurnCase(turnCaseValue)
    ? ""
    : "Turn Type is required.";
  const pointTypeError = hasRoutePointType(draft?.pointType, selectedMode)
    ? ""
    : "Point Type is required.";

  const multimedia = Array.isArray(draft?.multimedia) ? draft.multimedia : [];
  const imageItems = multimedia.filter(
    (item) => resolveMediaKind(item) === "image",
  );
  const audioItems = multimedia.filter(
    (item) =>
      resolveMediaKind(item) === "audio" &&
      String(item?.language || item?.languageIso || "").toLowerCase() ===
        currentLanguageIso,
  );
  const audioMediaCount = multimedia.filter(
    (item) => resolveMediaKind(item) === "audio",
  ).length;
  const mediaError = hasRoutePointMedia(draft)
    ? ""
    : "Add at least 1 image or audio.";

  return (
    <div className="mode-route-tool-point-editor">
      <div className="mode-route-tool-point-editor-head">
        <p className="mode-route-tool-subtitle">
          Edit point #{draft.order || "-"}
        </p>
        <button
          type="button"
          className="app-icon-btn app-icon-btn--delete"
          aria-label="Delete point"
          title="Delete point"
          onClick={() => onDeletePoint?.(draft?.id)}
        >
          <IconTrash size={14} />
        </button>
      </div>

      <div>
        <p className="tour-add-form-label">
          name
          {touched.name && nameError ? (
            <InlineError>{nameError}</InlineError>
          ) : null}
        </p>
        <label className="field-label app-form-field mode-route-field">
          <input
            className="field-input"
            value={currentNameValue}
            onChange={(event) => {
              const nextDraft = {
                ...(draft || {}),
                name: {
                  ...(draft?.name || {}),
                  [currentLanguageIso]: event.target.value,
                },
              };
              commitDraftImmediately(nextDraft);
              setTouched((prev) => ({
                ...prev,
                name: isNewPoint ? true : !hasRoutePointName(nextDraft.name, nextDraft),
              }));
            }}
            onBlur={() =>
              setTouched((prev) => ({
                ...prev,
                name: isNewPoint ? true : !hasRoutePointName(draft?.name, draft),
              }))
            }
          />
        </label>
      </div>

      <div>
        <p className="tour-add-form-label">description</p>
        <button
          type="button"
          className={`field-label app-form-field field-preview-row ${readLocalizedValue(draft?.description?.[currentLanguageIso]) ? "filled" : ""}`}
          onClick={() => openLocalizedEditor("description", currentLanguageIso)}
        >
          {readLocalizedValue(draft?.description?.[currentLanguageIso]) ? (
            <span
              className="field-preview-value field-preview-value-html"
              dangerouslySetInnerHTML={{
                __html: readLocalizedValue(
                  draft?.description?.[currentLanguageIso],
                ),
              }}
            />
          ) : (
            <span className="field-preview-value">Complete</span>
          )}
        </button>
      </div>

      <div>
        <p className="tour-add-form-label">more info</p>
        <button
          type="button"
          className={`field-label app-form-field field-preview-row ${readLocalizedValue(draft?.more_info?.[currentLanguageIso]) ? "filled" : ""}`}
          onClick={() => openLocalizedEditor("more_info", currentLanguageIso)}
        >
          {readLocalizedValue(draft?.more_info?.[currentLanguageIso]) ? (
            <span
              className="field-preview-value field-preview-value-html"
              dangerouslySetInnerHTML={{
                __html: readLocalizedValue(
                  draft?.more_info?.[currentLanguageIso],
                ),
              }}
            />
          ) : (
            <span className="field-preview-value">Complete</span>
          )}
        </button>
      </div>

      {selectedMode === "driving" ? (
        <>
          <label className="field-label app-form-field mode-route-field">
            <span className="label-inline">
              Radius meters
              {touched.radiusMeters && radiusError ? (
                <InlineError>{radiusError}</InlineError>
              ) : null}
            </span>
            <input
              className="field-input"
              type="number"
              min="0.01"
              step="1"
              value={Number(draft?.radiusMeters ?? 0) || ""}
              onChange={(event) => {
                const nextValue = Number(event.target.value || 0);
                const nextDraft = {
                  ...(draft || {}),
                  radiusMeters: nextValue,
                };
                commitDraftImmediately(nextDraft);
                setTouched((prev) => ({ ...prev, radiusMeters: true }));
              }}
              onBlur={() =>
                setTouched((prev) => ({ ...prev, radiusMeters: true }))
              }
            />
          </label>
          <label className="field-label app-form-field mode-route-field">
            <span className="label-inline">
              Turn Type
              {touched.turnCase && caseError ? (
                <InlineError>{caseError}</InlineError>
              ) : null}
            </span>
            <div className="mode-route-turn-type-picker">
              <button
                type="button"
                className="mode-route-turn-type-trigger"
                onClick={() => setIsTurnTypePickerOpen((prev) => !prev)}
              >
                <span className="mode-route-turn-type-trigger-main">
                  <ActiveTurnTypeIcon size={15} />
                  <span>
                    {activeTurnType
                      ? `${activeTurnType.label} (${activeTurnType.value})`
                      : "Select turn type"}
                  </span>
                </span>
                <IconChevronDown size={14} />
              </button>
              {isTurnTypePickerOpen ? (
                <div className="mode-route-turn-type-menu">
                  {TURN_TYPE_DEFINITIONS.map((option) => {
                    const IconComponent =
                      TURN_TYPE_ICON_BY_KEY[option.key] || IconMapPin;
                    const isActive =
                      Number(turnCaseValue) === Number(option.value);
                    return (
                      <button
                        key={option.value}
                        type="button"
                        className={`mode-route-turn-type-item ${isActive ? "active" : ""}`}
                        onClick={() => {
                          const nextDraft = {
                            ...(draft || {}),
                            turnCase: Number(option.value),
                          };
                          commitDraftImmediately(nextDraft);
                          setTouched((prev) => ({ ...prev, turnCase: true }));
                          setIsTurnTypePickerOpen(false);
                        }}
                      >
                        <IconComponent size={14} />
                        <span>{option.label}</span>
                        <small>{option.value}</small>
                      </button>
                    );
                  })}
                </div>
              ) : null}
            </div>
          </label>
        </>
      ) : null}
      {selectedMode === "driving" ? (
        <label className="field-label app-form-field mode-route-field">
          <span className="label-inline">Route Group</span>
          <div className="mode-route-turn-type-picker">
            <button
              type="button"
              className="mode-route-turn-type-trigger"
              onClick={() => setIsRouteGroupPickerOpen((prev) => !prev)}
            >
              <span className="mode-route-turn-type-trigger-main">
                <IconMapPin size={15} />
                <span>
                  {routeGroupSelectValue === "main"
                    ? "Main"
                    : `Route ${routeGroupSelectValue}`}
                </span>
              </span>
              <IconChevronDown size={14} />
            </button>
            {isRouteGroupPickerOpen ? (
              <div className="mode-route-turn-type-menu">
                <button
                  type="button"
                  className={`mode-route-turn-type-item ${routeGroupSelectValue === "main" ? "active" : ""}`}
                  onClick={() => {
                    const ownRouteGroup = "main";
                    commitDraftImmediately({
                      ...(draft || {}),
                      routeGroup: ownRouteGroup,
                      routeGroupName: buildDrivingRouteGroupName(
                        draft?.order,
                        ownRouteGroup,
                      ),
                      routeGroupParentPointId: "",
                    });
                    setIsRouteGroupPickerOpen(false);
                  }}
                >
                  <IconMapPin size={14} />
                  <span>Main</span>
                  <small>Continuation</small>
                </button>
                {drivingRouteGroupOptions.map((item) => (
                  <button
                    key={item}
                    type="button"
                    className={`mode-route-turn-type-item ${routeGroupSelectValue === String(item || "") ? "active" : ""}`}
                    onClick={() => {
                      commitDraftImmediately({
                        ...(draft || {}),
                        routeGroup: String(item || ""),
                        routeGroupName: buildDrivingRouteGroupName(
                          draft?.order,
                          item,
                        ),
                        routeGroupParentPointId: "",
                      });
                      setIsRouteGroupPickerOpen(false);
                    }}
                  >
                    <IconMapPin size={14} />
                    <span>Route {item}</span>
                    <small>
                      {buildDrivingRouteGroupName(draft?.order, item)}
                    </small>
                  </button>
                ))}
              </div>
            ) : null}
          </div>
          {routeGroupSelectValue !== "main" ? (
            <small className="field-help-text">
              Route group name:{" "}
              {buildDrivingRouteGroupName(draft?.order, routeGroupSelectValue)}
            </small>
          ) : null}
        </label>
      ) : null}
      {selectedMode === "random" ? (
        <label className="field-label app-form-field mode-route-field">
          <span className="label-inline">
            Point Type
            {touched.pointType && pointTypeError ? (
              <InlineError>{pointTypeError}</InlineError>
            ) : null}
          </span>
          <div className="mode-route-turn-type-picker">
            <button
              type="button"
              className="mode-route-turn-type-trigger"
              onClick={() => setIsPointTypePickerOpen((prev) => !prev)}
            >
              <span className="mode-route-turn-type-trigger-main">
                <ActivePointTypeIcon size={15} />
                <span>
                  {activePointType
                    ? activePointType.label
                    : "Select point type"}
                </span>
              </span>
              <IconChevronDown size={14} />
            </button>
            {isPointTypePickerOpen ? (
              <div className="mode-route-turn-type-menu">
                {POINT_TYPE_OPTIONS.map((option) => {
                  const isActive =
                    String(draft?.pointType || "") === option.value;
                  const IconComponent = option.Icon;
                  return (
                    <button
                      key={option.value}
                      type="button"
                      className={`mode-route-turn-type-item ${isActive ? "active" : ""}`}
                      onClick={() => {
                        const nextDraft = {
                          ...(draft || {}),
                          pointType: option.value,
                        };
                        commitDraftImmediately(nextDraft);
                        setTouched((prev) => ({ ...prev, pointType: true }));
                        setIsPointTypePickerOpen(false);
                      }}
                    >
                      <IconComponent size={14} />
                      <span>{option.label}</span>
                    </button>
                  );
                })}
              </div>
            ) : null}
          </div>
        </label>
      ) : null}

      <MediaPicker
        title="images"
        titleError={mediaError}
        items={imageItems}
        isUploading={false}
        uploadError=""
        accept={{ "image/*": [] }}
        invalidTypeMessage="Images only."
        onUpload={async (files) => {
          const uploaded = await onUploadPointMedia?.(files);
          if (!Array.isArray(uploaded) || !uploaded.length) return;
          const nextDraft = {
            ...(draft || {}),
            multimedia: [...(draft?.multimedia || []), ...uploaded],
          };
          commitDraftImmediately(nextDraft);
        }}
        onRemove={(mediaId) =>
          commitDraftImmediately({
            ...(draft || {}),
            multimedia: (draft?.multimedia || []).filter(
              (item) => item.id !== mediaId,
            ),
          })
        }
        uploadLabel="Upload image"
        uploadHint={
          mediaError
            ? "Required: upload at least 1 image or audio."
            : "Upload images for this mode point."
        }
      />

      <MediaPicker
        title="audios"
        items={audioItems}
        isUploading={false}
        uploadError=""
        accept={{ "audio/*": [] }}
        maxFiles={1}
        invalidTypeMessage="Audio only."
        onUpload={async (files) => {
          const uploaded = await onUploadPointMedia?.(files, {
            language: currentLanguageIso,
            mediaRole: "pointAudio",
          });
          if (!Array.isArray(uploaded) || !uploaded.length) return;
          const nextDraft = {
            ...(draft || {}),
            multimedia: [
              ...(draft?.multimedia || []).filter(
                (item) =>
                  !(
                    resolveMediaKind(item) === "audio" &&
                    String(
                      item?.language || item?.languageIso || "",
                    ).toLowerCase() === currentLanguageIso
                  ),
              ),
              ...uploaded,
            ],
          };
          commitDraftImmediately(nextDraft);
        }}
        onRemove={(mediaId) =>
          commitDraftImmediately({
            ...(draft || {}),
            multimedia: (draft?.multimedia || []).filter(
              (item) => item.id !== mediaId,
            ),
          })
        }
        uploadLabel={`Upload audio (${currentLanguageLabel})`}
        uploadHint="Upload one audio for the selected language."
      />

      <div>
        <p className="tour-add-form-label">notes</p>
        <label className="field-label app-form-field mode-route-field">
          <textarea
            className="field-input"
            rows={4}
            value={String(draft?.notes || "")}
            onChange={(event) =>
              commitDraftImmediately({
                ...(draft || {}),
                notes: event.target.value,
              })
            }
            placeholder="Add notes for this route point"
          />
        </label>
      </div>

      <AppModal
        isOpen={editor.isOpen}
        onClose={closeLocalizedEditor}
        title={`${String(editor.field || "description").replace("_", " ")} (${editor.languageIso})`}
        size="lg"
      >
        <div className="tour-add-editor">
          {!isDescriptionEditorReady ? (
            <p className="tour-add-editor-loading">Loading editor...</p>
          ) : null}
          <div ref={setQuillHost} className="tour-add-quill" />
          <div className="tour-add-editor-actions">
            <button
              type="button"
              className="button-primary"
              onClick={saveLocalizedEditor}
            >
              Save
            </button>
            <button
              type="button"
              className="button-ghost"
              onClick={closeLocalizedEditor}
            >
              Cancel
            </button>
          </div>
        </div>
      </AppModal>
    </div>
  );
}

export default ModeToolPointEditor;
